import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Zap, Brain, Swords, Shield, Target, Trophy, BookOpen, BarChart3, TrendingUp,
  ChevronRight, Download, Sparkles, ArrowUpRight, Flame, Crown, Quote,
} from "lucide-react";
import { Reveal } from "../components/Reveal";
import { CountUp } from "../components/CountUp";
import Seo from "../components/Seo";

const PHILOSOPHY = [
  { quote: "Discipline should be visible.", accent: "gold" },
  { quote: "Modern social media rewards attention. ASCENDXP rewards growth.", accent: "indigo" },
  { quote: "Status should not be bought. It should be earned.", accent: "gold" },
  { quote: "You do not rise to your goals. You fall to your systems.", accent: "indigo" },
  { quote: "Every action compounds. Every choice shapes rank.", accent: "gold" },
  { quote: "Stop existing. Start ascending.", accent: "indigo" },
];

const PHILOSOPHY_BADGES = ["AI Powered", "Gen Z Built", "Performance Driven", "Premium Experience"];

const ECOSYSTEM = [
  { to: 5, suffix: "", title: "Core Growth Pillars", accent: "gold",
    label: "PHYSIQUE • DISCIPLINE • KNOWLEDGE • SOCIAL • MIND" },
  { to: 6, suffix: "", title: "PvP Battle Modes", accent: "indigo",
    label: "RANKED • CLAN WARS • 1V1 • WAGERS • SEASONAL • FRIEND CHALLENGES" },
  { to: 100, suffix: "+", title: "Challenge Templates", accent: "gold",
    label: "FITNESS • STUDY • PRODUCTIVITY • WELLNESS • SOCIAL" },
  { to: 24, suffix: "/7", title: "AI Intelligence", accent: "indigo",
    label: "REAL-TIME INSIGHTS • COACHING • ANALYTICS • RECOMMENDATIONS" },
];

const ECOSYSTEM_BADGES = ["AI Powered", "Real-Time PvP", "Gen Z Built", "Premium Experience"];

const FEATURES = [
  { icon: Zap, title: "Aura Scoring System", desc: "Your living reputation. Every disciplined action raises your Aura and rank.", span: "lg:col-span-2", accent: "indigo" },
  { icon: Brain, title: "AI Coach", desc: "A personal coach that adapts to your goals in real time.", span: "", accent: "gold" },
  { icon: Swords, title: "Battle Arena PvP", desc: "Challenge rivals head-to-head and prove your discipline.", span: "", accent: "indigo" },
  { icon: Shield, title: "Clans", desc: "Build and lead clans. Rise together, dominate together.", span: "", accent: "indigo" },
  { icon: Target, title: "Challenges", desc: "Daily & seasonal challenges engineered to push your limits.", span: "", accent: "gold" },
  { icon: Trophy, title: "Leaderboards", desc: "Climb global and clan leaderboards in real time.", span: "lg:col-span-2", accent: "indigo" },
  { icon: BookOpen, title: "Stories", desc: "Share your ascent. Inspire the community.", span: "", accent: "indigo" },
  { icon: BarChart3, title: "Analytics", desc: "Deep insights into streaks, habits, and momentum.", span: "", accent: "gold" },
  { icon: TrendingUp, title: "Rank Progression", desc: "Ascend through elite ranks with prestige and rewards.", span: "", accent: "indigo" },
];

function FloatingWidget({ className, delay, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, delay }}
      className={`absolute glass rounded-2xl p-4 hidden lg:block ${className}`}
    >
      <div className="animate-float" style={{ animationDelay: `${delay}s` }}>{children}</div>
    </motion.div>
  );
}

export default function Landing() {
  return (
    <div data-testid="landing-page">
      <Seo title="ASCENDXP — Where Discipline Becomes Status" description="Gamified self-improvement. Earn Aura, climb leaderboards, battle rivals, and ascend the ranks." />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16">
        <div className="absolute inset-0 grid-overlay opacity-60" />
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-indigo-600/20 blur-[120px]" />
        <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] rounded-full bg-[#d4af37]/10 blur-[120px]" />
        <img
          src="https://images.unsplash.com/photo-1654198340681-a2e0fc449f1b"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-screen"
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs font-medium text-zinc-300 mb-8"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#d4af37]" />
            The gamified self-improvement arena
          </motion.div>

          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-4xl">
            WHERE DISCIPLINE
            <br />
            BECOMES <span className="text-gradient-gold">STATUS</span>
          </h1>

          <p className="mt-6 text-base sm:text-lg text-zinc-400 max-w-xl leading-relaxed">
            ASCENDXP turns self-improvement into a sport. Earn Aura with AI coaching,
            battle rivals in PvP arenas, climb elite leaderboards, and track your ascent
            with deep analytics.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a href="#download" data-testid="hero-download-btn" className="btn-gold">
              <Download className="w-5 h-5" /> Download App
            </a>
            <Link to="/pricing" data-testid="hero-upgrade-btn" className="btn-primary">
              View Plans <ChevronRight className="w-5 h-5" />
            </Link>
            <a href="#features" data-testid="hero-explore-btn" className="btn-ghost">
              Explore Features
            </a>
          </div>

          {/* Floating gamified widgets */}
          <div className="relative h-0">
            <FloatingWidget className="top-[-340px] right-10 w-56" delay={0.3}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#d4af37] to-[#b8860b] grid place-items-center">
                  <Crown className="w-5 h-5 text-black" />
                </div>
                <div>
                  <div className="text-xs text-zinc-400">Your Rank</div>
                  <div className="font-heading font-bold text-[#d4af37]">ASCENDANT</div>
                </div>
              </div>
            </FloatingWidget>

            <FloatingWidget className="top-[-180px] right-44 w-52" delay={0.5}>
              <div>
                <div className="flex justify-between text-xs text-zinc-400 mb-2">
                  <span>Aura Meter</span><span className="text-indigo-400">9,420</span>
                </div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full w-[82%] rounded-full bg-gradient-to-r from-indigo-500 to-indigo-300" />
                </div>
                <div className="mt-3 flex items-center gap-1 text-xs text-emerald-400">
                  <Flame className="w-3.5 h-3.5" /> 47-day streak
                </div>
              </div>
            </FloatingWidget>

            <FloatingWidget className="top-[-60px] right-16 w-48" delay={0.7}>
              <div className="text-xs text-zinc-400 mb-2">Leaderboard</div>
              {[["1", "Aarav", "9.8k"], ["2", "Zoya", "9.4k"], ["3", "You", "9.4k"]].map(([r, n, a]) => (
                <div key={r} className="flex justify-between text-sm py-1">
                  <span className="text-zinc-300"><span className="text-[#d4af37] font-bold mr-2">{r}</span>{n}</span>
                  <span className="font-mono text-indigo-300">{a}</span>
                </div>
              ))}
            </FloatingWidget>
          </div>
        </div>
      </section>

      {/* THE ASCENDXP ECOSYSTEM */}
      <section className="relative py-24 border-y border-white/5">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full bg-indigo-600/8 blur-[120px]" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <Reveal className="text-center max-w-3xl mx-auto">
            <p className="text-xs uppercase tracking-[0.3em] text-indigo-400 font-bold mb-4">The Platform</p>
            <h2 className="font-heading font-black text-3xl sm:text-5xl tracking-tight">
              THE ASCENDXP <span className="text-gradient-gold">ECOSYSTEM</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-zinc-400 leading-relaxed">
              ASCENDXP isn't another social app. It is a performance operating system
              engineered to turn discipline into status.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {ECOSYSTEM.map((c, i) => {
              const gold = c.accent === "gold";
              return (
                <Reveal key={c.title} delay={i * 0.1}>
                  <div className="group glass rounded-2xl p-7 h-full text-center relative overflow-hidden hover:-translate-y-1 transition-transform duration-300">
                    <div className={`absolute -top-12 left-1/2 -translate-x-1/2 w-32 h-32 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity ${gold ? "bg-[#d4af37]/20" : "bg-indigo-600/20"}`} />
                    <div className="relative">
                      <CountUp
                        to={c.to}
                        suffix={c.suffix}
                        className="block font-heading font-black text-4xl sm:text-5xl text-gradient-gold"
                      />
                      <h3 className="mt-3 font-heading font-bold text-base text-white">{c.title}</h3>
                      <p className={`mt-4 text-[10px] leading-relaxed tracking-[0.12em] font-semibold ${gold ? "text-[#d4af37]/80" : "text-indigo-300/80"}`}>
                        {c.label}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Secondary premium badges */}
          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
              {ECOSYSTEM_BADGES.map((b) => (
                <span key={b} className="glass rounded-full px-4 py-2 text-xs font-medium text-zinc-300 inline-flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37] animate-pulse-glow" />
                  {b}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="relative py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-indigo-400 font-bold mb-4">The Arsenal</p>
            <h2 className="font-heading font-bold text-3xl sm:text-5xl tracking-tight max-w-2xl">
              Everything you need to <span className="text-gradient-indigo">ascend</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              const gold = f.accent === "gold";
              return (
                <Reveal key={f.title} delay={(i % 3) * 0.08} className={f.span}>
                  <div className="group glass rounded-2xl p-7 h-full hover:-translate-y-1 transition-transform duration-300 relative overflow-hidden">
                    <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity ${gold ? "bg-[#d4af37]/20" : "bg-indigo-600/20"}`} />
                    <div className={`w-12 h-12 rounded-xl grid place-items-center mb-5 ${gold ? "bg-[#d4af37]/15 text-[#d4af37]" : "bg-indigo-600/15 text-indigo-400"}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-heading font-bold text-lg mb-2">{f.title}</h3>
                    <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY ASCENDXP */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="glass rounded-3xl p-10 border-l-2 border-red-500/40">
              <p className="text-xs uppercase tracking-[0.3em] text-red-400 font-bold mb-4">The Problem</p>
              <h3 className="font-heading font-bold text-2xl sm:text-3xl mb-4">Social media rewards vanity</h3>
              <p className="text-zinc-400 leading-relaxed">
                Endless scrolling, fake highlight reels, and dopamine traps. The modern feed
                profits from your distraction — not your growth. Status is faked, not earned.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="glass rounded-3xl p-10 border-l-2 border-indigo-500/60 glow-indigo">
              <p className="text-xs uppercase tracking-[0.3em] text-indigo-400 font-bold mb-4">The ASCENDXP Way</p>
              <h3 className="font-heading font-bold text-2xl sm:text-3xl mb-4">We reward discipline</h3>
              <p className="text-zinc-400 leading-relaxed">
                ASCENDXP makes real progress visible and competitive. Earn Aura for consistency,
                rank up through verified effort, and turn discipline into a status worth flexing.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THE ASCENDXP PHILOSOPHY */}
      <section data-testid="philosophy-section" className="relative py-24 sm:py-32 overflow-hidden">
        {/* Floating particles */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(14)].map((_, i) => (
            <motion.span
              key={i}
              className={`absolute rounded-full ${i % 2 ? "bg-[#d4af37]/40" : "bg-indigo-400/40"}`}
              style={{
                width: `${2 + (i % 3)}px`,
                height: `${2 + (i % 3)}px`,
                left: `${(i * 67) % 100}%`,
                top: `${(i * 39) % 100}%`,
              }}
              animate={{ y: [0, -24, 0], opacity: [0.2, 0.8, 0.2] }}
              transition={{ duration: 5 + (i % 4), repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
            />
          ))}
        </div>
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-indigo-600/10 blur-[130px]" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] font-bold mb-4">Manifesto</p>
            <h2 className="font-heading font-black text-3xl sm:text-5xl tracking-tight">
              THE ASCENDXP <span className="text-gradient-gold">PHILOSOPHY</span>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-zinc-400">Built for those who refuse mediocrity.</p>
          </Reveal>

          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PHILOSOPHY.map((p, i) => {
              const gold = p.accent === "gold";
              return (
                <Reveal key={i} delay={(i % 3) * 0.1} y={36}>
                  <div className="group relative glass rounded-2xl p-8 h-full overflow-hidden transition-transform duration-300 hover:-translate-y-1">
                    <div className={`absolute -top-16 -right-10 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${gold ? "bg-[#d4af37]/20" : "bg-indigo-600/25"}`} />
                    <Quote className={`w-10 h-10 mb-5 ${gold ? "text-[#d4af37]/70" : "text-indigo-400/70"}`} fill="currentColor" />
                    <p className="relative font-heading font-bold text-xl sm:text-2xl leading-snug tracking-tight">
                      <span className={gold ? "text-gradient-gold" : "text-white"}>{p.quote}</span>
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Trust badges */}
          <Reveal delay={0.15}>
            <div className="mt-14 flex flex-wrap items-center justify-center gap-3">
              {PHILOSOPHY_BADGES.map((b) => (
                <span key={b} className="glass rounded-full px-4 py-2 text-xs font-medium text-zinc-300 inline-flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37] animate-pulse-glow" />
                  {b}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <p className="mt-12 text-center text-zinc-400 max-w-2xl mx-auto leading-relaxed">
              ASCENDXP is not another social platform. It is a performance operating system
              engineered to convert discipline into measurable status.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA / DOWNLOAD */}
      <section id="download" className="relative py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="relative glass rounded-3xl p-12 sm:p-16 text-center overflow-hidden glow-indigo">
              <div className="absolute inset-0 grid-overlay opacity-40" />
              <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full bg-indigo-600/20 blur-[100px]" />
              <div className="relative">
                <h2 className="font-heading font-black text-3xl sm:text-5xl tracking-tight">
                  Begin your <span className="text-gradient-indigo">ascent</span>
                </h2>
                <p className="mt-4 text-zinc-400 max-w-lg mx-auto">
                  Download ASCENDXP and start earning status the right way — or go premium and
                  unlock the full arena.
                </p>
                <div className="mt-8 flex flex-wrap gap-4 justify-center">
                  <a href="#" className="btn-gold"><Download className="w-5 h-5" /> Download App</a>
                  <Link to="/pricing" className="btn-primary">View Plans <ArrowUpRight className="w-5 h-5" /></Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
